import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BiBell,
  BiBulb,
  BiPlus,
  BiChevronDown,
  BiChevronUp,
  BiX,
  BiSearch,
} from "react-icons/bi";
import {
  AiOutlineUser,
  AiOutlineClockCircle,
  AiOutlineFieldTime,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
} from "react-icons/ai";
import { FiSend, FiAlertCircle, FiBarChart2 } from "react-icons/fi";
import { IoIosMailOpen, IoIosMail } from "react-icons/io";
import "../css/Campaigns.css";
import ResendCampaignModal from "../component/Campaign/ResendCampaign";

const Notification = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [analyticsModal, setAnalyticsModal] = useState(null);
  const [statusModal, setStatusModal] = useState(null); // New state for status modal
  const [searchTerm, setSearchTerm] = useState("");
  const [statusSearchTerm, setStatusSearchTerm] = useState(""); // Separate search for status modal
  const [campaignSearch, setCampaignSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const navigate = useNavigate();
  const [resendModal, setResendModal] = useState(null);
  const [statusSummary, setStatusSummary] = useState({});
  const [contactStatuses, setContactStatuses] = useState({});
  const [deletingCampaignId, setDeletingCampaignId] = useState(null);

  const tenantName = localStorage.getItem("tenantName");

  // Tab options
  const tabs = [
    { id: "all", name: "All Campaigns", icon: <BiBulb /> },
    { id: "scheduled", name: "Scheduled", icon: <AiOutlineFieldTime /> },
    { id: "active", name: "Active", icon: <FiSend /> },
    { id: "sent", name: "Sent", icon: <AiOutlineCheckCircle /> },
    { id: "failed", name: "Failed", icon: <AiOutlineCloseCircle /> },
  ];

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/campaigns/list`,
          {
            method: "GET",
            headers: {
              "x-email": localStorage.getItem("tenantEmail"),
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch campaigns");
        }
        const data = await response.json();
        setCampaigns(data);

        // Extract status summary from the API response
        const summary = {};
        const statuses = {};

        data.forEach((c) => {
          // API already provides statusSummary
          summary[c._id] = c.statusSummary || {
            sent: 0,
            delivered: 0,
            read: 0,
            failed: 0,
          };

          // API already provides contactStatuses
          if (c.contactStatuses) {
            statuses[c._id] = c.contactStatuses;
          }
        });

        setStatusSummary(summary);
        setContactStatuses(statuses);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const statusCounts = useMemo(() => {
    return campaigns.reduce(
      (acc, campaign) => {
        const status = (campaign?.status || "sent").toLowerCase();
        if (acc[status] !== undefined) {
          acc[status] += 1;
        } else {
          acc[status] = 1;
        }
        acc.all += 1;
        return acc;
      },
      { all: 0, scheduled: 0, active: 0, sent: 0, failed: 0 }
    );
  }, [campaigns]);

  // Filter campaigns based on active tab, search, and sort
  const filteredCampaigns = useMemo(() => {
    const normalizedSearch = campaignSearch.trim().toLowerCase();
    const filtered = campaigns.filter((campaign) => {
      const status = (campaign?.status || "sent").toLowerCase();
      const matchesTab = activeTab === "all" || status === activeTab;
      if (!normalizedSearch) return matchesTab;
      const name = campaign?.name || "";
      const templateId = campaign?.templateId || "";
      const combined = `${name} ${templateId} ${status}`.toLowerCase();
      return matchesTab && combined.includes(normalizedSearch);
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sortBy === "name") {
        return (a?.name || "").localeCompare(b?.name || "");
      }
      if (sortBy === "contacts") {
        return (b?.contacts?.length || 0) - (a?.contacts?.length || 0);
      }
      const aDate = new Date(a?.schedule || a?.createdAt || 0).getTime();
      const bDate = new Date(b?.schedule || b?.createdAt || 0).getTime();
      return bDate - aDate;
    });

    return sorted;
  }, [campaigns, activeTab, campaignSearch, sortBy]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const toggleCampaignExpand = (id) => {
    setExpandedCampaign(expandedCampaign === id ? null : id);
  };

  const getStatusIcon = (status) => {
    switch ((status || "sent").toLowerCase()) {
      case "scheduled":
        return <AiOutlineFieldTime className="text-warning" />;
      case "active":
        return <FiSend className="text-info" />;
      case "sent":
        return <AiOutlineCheckCircle className="text-success" />;
      case "failed":
        return <AiOutlineCloseCircle className="text-danger" />;
      default:
        return <AiOutlineCheckCircle className="text-primary" />;
    }
  };

  const handleCreateNewCampaign = () => {
    navigate(`/${tenantName}/campaign/create`);
  };

  // Open analytics modal
  const openAnalyticsModal = (campaign) => {
    setAnalyticsModal(campaign);
    setSearchTerm("");
  };

  // Close analytics modal
  const closeAnalyticsModal = () => {
    setAnalyticsModal(null);
  };

  // Open status modal
  const openStatusModal = (campaign) => {
    setStatusModal(campaign);
    setStatusSearchTerm("");
  };

  // Close status modal
  const closeStatusModal = () => {
    setStatusModal(null);
  };

  const openResendModal = (campaign) => setResendModal(campaign);
  const closeResendModal = () => setResendModal(null);

  const handleDeleteCampaign = async (campaignId) => {
    if (!campaignId) return;
    const confirmed = window.confirm(
      "Are you sure you want to delete this campaign?"
    );
    if (!confirmed) return;

    try {
      setDeletingCampaignId(campaignId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/campaigns/${campaignId}`,
        {
          method: "DELETE",
          headers: {
            "x-email": localStorage.getItem("tenantEmail"),
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete campaign");
      }

      setCampaigns((prev) => prev.filter((c) => c._id !== campaignId));
      setStatusSummary((prev) => {
        const next = { ...prev };
        delete next[campaignId];
        return next;
      });
      setContactStatuses((prev) => {
        const next = { ...prev };
        delete next[campaignId];
        return next;
      });
      if (expandedCampaign === campaignId) {
        setExpandedCampaign(null);
      }
    } catch (err) {
      setError(err.message || "Failed to delete campaign");
    } finally {
      setDeletingCampaignId(null);
    }
  };

  // Get contact engagement data in simple row format
  const getContactEngagementData = (campaign) => {
    if (!campaign) return [];

    const allContacts = campaign.contacts || [];
    const rawMetrics = campaign.metrics?.buttonClicks || {};
    const metrics =
      rawMetrics && typeof rawMetrics === "object" && rawMetrics instanceof Map
        ? Object.fromEntries(rawMetrics)
        : rawMetrics;

    return allContacts.map((contact) => {
      const normalized = contact.replace(/^(\+91)/, "");

      let clicked = "No";
      let totalClicks = 0;
      let buttonClicks = {};

      Object.entries(metrics || {}).forEach(([btnIndex, btnData]) => {
        if (!btnData.contacts) return;

        // Match any format
        const count =
          btnData.contacts[contact] ??
          btnData.contacts[`+91${normalized}`] ??
          btnData.contacts[normalized];

        if (count) {
          clicked = "Yes";
          totalClicks += count;
          buttonClicks[btnIndex] = count;
        } else {
          buttonClicks[btnIndex] = 0;
        }
      });

      return {
        mobileNumber: contact,
        clicked,
        totalClicks,
        buttonClicks,
      };
    });
  };

  // Get contact status data for the status modal
  const getContactStatusData = (campaign) => {
  if (!campaign) return [];

  const allContacts = campaign.contacts || [];
  const statuses = campaign.contactStatuses || {};

  return allContacts.map((contact) => {
    // Try multiple key formats since APIs sometimes normalize differently
    const normalized = contact.replace(/^(\+91)/, "");
    const withPlus91 = `+91${normalized}`;
    
    // Try lookup in multiple formats
    const status = 
      statuses[contact] || 
      statuses[withPlus91] || 
      statuses[normalized] || 
      "sent";

    let icon, color, displayText;
    switch (status.toLowerCase()) {
      case "delivered":
        icon = <IoIosMail className="me-1" />;
        color = "text-success";
        displayText = "Delivered";
        break;
      case "read":
        icon = <IoIosMailOpen className="me-1" />;
        color = "text-primary";
        displayText = "Read";
        break;
      case "failed":
        icon = <AiOutlineCloseCircle className="me-1" />;
        color = "text-danger";
        displayText = "Failed";
        break;
      case "sent":
      default:
        icon = <FiSend className="me-1" />;
        color = "text-info";
        displayText = "Sent";
    }

    return {
      mobileNumber: contact,
      status,
      displayText,
      icon,
      color,
      lastUpdated: campaign.updatedAt || campaign.createdAt,
    };
  });
};
  // Filter engagement data for search
  const filteredEngagementData = useMemo(() => {
    if (!analyticsModal) return [];
    const engagementData = getContactEngagementData(analyticsModal);
    if (!searchTerm) return engagementData;

    return engagementData.filter((contact) =>
      contact.mobileNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [analyticsModal, searchTerm]);

  // Filter status data for search
  const filteredStatusData = useMemo(() => {
    if (!statusModal) return [];
    const statusData = getContactStatusData(statusModal);
    if (!statusSearchTerm) return statusData;

    return statusData.filter(
      (contact) =>
        contact.mobileNumber
          .toLowerCase()
          .includes(statusSearchTerm.toLowerCase()) ||
        contact.displayText
          .toLowerCase()
          .includes(statusSearchTerm.toLowerCase())
    );
  }, [statusModal, statusSearchTerm]);

  // Get summary stats for analytics
  const getAnalyticsSummaryStats = (engagementData) => {
    const totalContacts = engagementData.length;
    const clickedContacts = engagementData.filter(
      (contact) => contact.clicked === "Yes"
    ).length;
    const notClickedContacts = totalContacts - clickedContacts;
    const totalClicks = engagementData.reduce(
      (sum, contact) => sum + contact.totalClicks,
      0
    );
    const clickRate =
      totalContacts > 0
        ? ((clickedContacts / totalContacts) * 100).toFixed(1)
        : 0;

    return {
      totalContacts,
      clickedContacts,
      notClickedContacts,
      totalClicks,
      clickRate,
    };
  };

  // Get summary stats for status modal
  const getStatusSummaryStats = (statusData) => {
    const totalContacts = statusData.length;
    const delivered = statusData.filter((c) => c.status === "delivered").length;
    const read = statusData.filter((c) => c.status === "read").length;
    const sent = statusData.filter((c) => c.status === "sent").length;
    const failed = statusData.filter((c) => c.status === "failed").length;

    const deliveryRate =
      totalContacts > 0 ? ((delivered / totalContacts) * 100).toFixed(1) : 0;
    const readRate =
      totalContacts > 0 ? ((read / totalContacts) * 100).toFixed(1) : 0;

    return {
      totalContacts,
      delivered,
      read,
      sent,
      failed,
      deliveryRate,
      readRate,
    };
  };

  // Analytics Modal Component
  const AnalyticsModal = ({ campaign, onClose }) => {
    const engagementData = getContactEngagementData(campaign);
    const stats = getAnalyticsSummaryStats(engagementData);
    const metricsButtons = campaign?.metrics?.buttonClicks || {};
    const buttonKeys = Object.keys(metricsButtons || {});

    return (
      <div className="cx-campaigns-modal-overlay" onClick={onClose}>
        <div
          className="cx-campaigns-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cx-campaigns-modal-header">
            <div className="cx-campaigns-modal-title">
              <FiBarChart2 className="me-2" />
              <div>
                <h4>{campaign.name} - Click Analytics</h4>
                <p className="cx-campaigns-modal-subtitle">
                  Template: {campaign.templateId}
                </p>
              </div>
            </div>
            <button className="cx-campaigns-modal-close" onClick={onClose}>
              <BiX />
            </button>
          </div>

          <div className="cx-campaigns-modal-content">
            {/* Quick Stats */}
            <div className="cx-campaigns-quick-stats">
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number">
                  {stats.totalContacts}
                </span>
                <span className="cx-campaigns-stat-label">Total Contacts</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number text-success">
                  {stats.clickedContacts}
                </span>
                <span className="cx-campaigns-stat-label">Clicked</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number text-danger">
                  {stats.notClickedContacts}
                </span>
                <span className="cx-campaigns-stat-label">Not Clicked</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number">
                  {stats.totalClicks}
                </span>
                <span className="cx-campaigns-stat-label">Total Clicks</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number">
                  {stats.clickRate}%
                </span>
                <span className="cx-campaigns-stat-label">Click Rate</span>
              </div>
            </div>

            {/* Search Box */}
            <div className="cx-campaigns-search-section">
              <div className="cx-campaigns-search-box">
                <BiSearch className="cx-campaigns-search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by mobile number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <span className="cx-campaigns-search-count">
                {filteredEngagementData.length} of {engagementData.length}{" "}
                contacts
              </span>
            </div>

            {/* Engagement Table */}
            <div className="cx-campaigns-table-container">
              <table className="cx-campaigns-table">
                <thead>
                  <tr>
                    <th>Mobile Number</th>

                    {/* Dynamic button headers */}
                    {buttonKeys.map((btn) => (
                        <th key={btn}>Button {btn}</th>
                      ))}

                    <th>Total Clicks</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEngagementData.map((contact, index) => (
                    <tr key={index}>
                      <td>{contact.mobileNumber}</td>

                      {/* Per-button click counts */}
                      {buttonKeys.map((btn) => (
                        <td key={btn}>
                          {contact.buttonClicks[btn] > 0 ? (
                            contact.buttonClicks[btn]
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      ))}

                      <td>
                        {contact.totalClicks > 0 ? (
                          contact.totalClicks
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      <td>
                        {contact.clicked === "Yes" ? (
                          <span className="text-success">
                            <AiOutlineCheckCircle className="me-1" /> Clicked
                          </span>
                        ) : (
                          <span className="text-danger">
                            <AiOutlineCloseCircle className="me-1" /> Not
                            Clicked
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredEngagementData.length === 0 && (
                <div className="cx-campaigns-no-data">
                  <BiSearch className="cx-campaigns-no-data-icon" />
                  <p>No contacts found matching your search</p>
                </div>
              )}
            </div>
          </div>

          <div className="cx-campaigns-modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                // Simple CSV export
                const headers = ["Mobile Number", "Status", "Total Clicks"];
                const csvData = filteredEngagementData.map((contact) => [
                  contact.mobileNumber,
                  contact.clicked,
                  contact.totalClicks,
                ]);

                const csvContent = [
                  headers.join(","),
                  ...csvData.map((row) => row.join(",")),
                ].join("\n");

                const blob = new Blob([csvContent], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `engagement_${campaign.name}_${
                  new Date().toISOString().split("T")[0]
                }.csv`;
                link.click();
              }}
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>
    );
  };

 // Message Status Modal Component
const MessageStatusModal = ({ campaign, onClose }) => {
    const statusData = getContactStatusData(campaign);
    const stats = getStatusSummaryStats(statusData);

    return (
      <div className="cx-campaigns-modal-overlay" onClick={onClose}>
        <div
          className="cx-campaigns-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cx-campaigns-modal-header">
            <div className="cx-campaigns-modal-title">
              <IoIosMail className="me-2" />
              <div>
                <h4>{campaign.name} - Message Status</h4>
                <p className="cx-campaigns-modal-subtitle">
                  Delivery status for all contacts
                </p>
              </div>
            </div>
            <button className="cx-campaigns-modal-close" onClick={onClose}>
              <BiX />
            </button>
          </div>

          <div className="cx-campaigns-modal-content">
            {/* Quick Stats */}
            <div className="cx-campaigns-quick-stats">
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number">
                  {stats.totalContacts}
                </span>
                <span className="cx-campaigns-stat-label">Total Contacts</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number text-success">
                  {stats.delivered}
                </span>
                <span className="cx-campaigns-stat-label">Delivered</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number text-primary">
                  {stats.read}
                </span>
                <span className="cx-campaigns-stat-label">Read</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number text-info">
                  {stats.sent}
                </span>
                <span className="cx-campaigns-stat-label">Sent</span>
              </div>
              <div className="cx-campaigns-stat-item">
                <span className="cx-campaigns-stat-number text-danger">
                  {stats.failed}
                </span>
                <span className="cx-campaigns-stat-label">Failed</span>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="cx-campaigns-status-summary">
              <div className="cx-campaigns-status-row">
                <div className="cx-campaigns-status-col">
                  <span className="cx-campaigns-status-label">Delivery Rate:</span>
                  <span className="cx-campaigns-status-value">
                    {stats.deliveryRate}%
                  </span>
                </div>
                <div className="cx-campaigns-status-col">
                  <span className="cx-campaigns-status-label">Read Rate:</span>
                  <span className="cx-campaigns-status-value">
                    {stats.readRate}%
                  </span>
                </div>
                <div className="cx-campaigns-status-col">
                  <span className="cx-campaigns-status-label">Failure Rate:</span>
                  <span className="cx-campaigns-status-value">
                    {stats.totalContacts > 0
                      ? ((stats.failed / stats.totalContacts) * 100).toFixed(1)
                      : 0}
                    %
                  </span>
                </div>
              </div>
            </div>

            {/* Search Box */}
            <div className="cx-campaigns-search-section">
              <div className="cx-campaigns-search-box">
                <BiSearch className="cx-campaigns-search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by mobile number or status..."
                  value={statusSearchTerm}
                  onChange={(e) => setStatusSearchTerm(e.target.value)}
                />
              </div>
              <span className="cx-campaigns-search-count">
                {filteredStatusData.length} of {statusData.length} contacts
              </span>
            </div>

            {/* Status Table */}
            <div className="cx-campaigns-table-container">
              <table className="cx-campaigns-table">
                <thead>
                  <tr>
                    <th>Mobile Number</th>
                    <th>Message Status</th>
                    <th>Last Updated</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredStatusData.map((contact, index) => (
                    <tr key={index}>
                      <td>{contact.mobileNumber}</td>
                      <td>
                        <span className={contact.color}>
                          {contact.icon}
                          {contact.displayText}
                        </span>
                      </td>
                      <td>
                        <span className="text-muted">
                          {formatDate(campaign.updatedAt || campaign.createdAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredStatusData.length === 0 && (
                <div className="cx-campaigns-no-data">
                  <BiSearch className="cx-campaigns-no-data-icon" />
                  <p>No contacts found matching your search</p>
                </div>
              )}
            </div>
          </div>

          <div className="cx-campaigns-modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                // CSV export for status data
                const headers = ["Mobile Number", "Status", "Last Updated"];
                const csvData = filteredStatusData.map((contact) => [
                  contact.mobileNumber,
                  contact.displayText,
                  formatDate(campaign.updatedAt || campaign.createdAt),
                ]);

                const csvContent = [
                  headers.join(","),
                  ...csvData.map((row) => row.join(",")),
                ].join("\n");

                const blob = new Blob([csvContent], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `status_${campaign.name}_${
                  new Date().toISOString().split("T")[0]
                }.csv`;
                link.click();
              }}
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="cx-campaigns-page">
        <div className="cx-campaigns-loading">
          <div className="spinner-grow text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="cx-campaigns-loading-text">
            Loading your campaigns...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cx-campaigns-page">
        <div className="cx-campaigns-error-card">
          <div className="cx-campaigns-error-icon">
            <FiAlertCircle />
          </div>
          <h5 className="cx-campaigns-error-title">
            Oops! Something went wrong
          </h5>
          <p className="cx-campaigns-error-message">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cx-campaigns-page">
      {/* Analytics Modal */}
      {analyticsModal && (
        <AnalyticsModal
          campaign={analyticsModal}
          onClose={closeAnalyticsModal}
        />
      )}

      {/* Message Status Modal */}
      {statusModal && (
        <MessageStatusModal campaign={statusModal} onClose={closeStatusModal} />
      )}

      {resendModal && (
        <ResendCampaignModal
          campaign={resendModal}
          onClose={closeResendModal}
        />
      )}

      {/* Header Section */}
      <div className="cx-campaigns-header">
        <div className="cx-campaigns-header-content">
          <div className="cx-campaigns-title">
            <BiBell className="cx-campaigns-title-icon" />
            <h1>Campaign Management</h1>
            <span className="badge bg-light text-dark ms-2">
              {statusCounts.all}
            </span>
          </div>
          <div className="cx-campaigns-header-actions">
            <button
              className="btn btn-primary"
              onClick={handleCreateNewCampaign}
            >
              <BiPlus className="me-2" /> New Campaign
            </button>
          </div>
        </div>
        <p className="cx-campaigns-description">
          Create, manage, and track performance of your notification campaigns.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="cx-campaigns-stats">
        <div className="cx-campaigns-stat-card">
          <div className="cx-campaigns-stat-icon">
            <BiBell />
          </div>
          <div>
            <p>Total Campaigns</p>
            <h4>{statusCounts.all}</h4>
          </div>
        </div>
        <div className="cx-campaigns-stat-card">
          <div className="cx-campaigns-stat-icon text-warning">
            <AiOutlineFieldTime />
          </div>
          <div>
            <p>Scheduled</p>
            <h4>{statusCounts.scheduled}</h4>
          </div>
        </div>
        <div className="cx-campaigns-stat-card">
          <div className="cx-campaigns-stat-icon text-info">
            <FiSend />
          </div>
          <div>
            <p>Active</p>
            <h4>{statusCounts.active}</h4>
          </div>
        </div>
        <div className="cx-campaigns-stat-card">
          <div className="cx-campaigns-stat-icon text-success">
            <AiOutlineCheckCircle />
          </div>
          <div>
            <p>Sent</p>
            <h4>{statusCounts.sent}</h4>
          </div>
        </div>
        <div className="cx-campaigns-stat-card">
          <div className="cx-campaigns-stat-icon text-danger">
            <AiOutlineCloseCircle />
          </div>
          <div>
            <p>Failed</p>
            <h4>{statusCounts.failed}</h4>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="cx-campaigns-filters">
        <div className="cx-campaigns-search">
          <BiSearch className="cx-campaigns-search-icon" />
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, template, or status..."
            value={campaignSearch}
            onChange={(e) => setCampaignSearch(e.target.value)}
          />
          {campaignSearch && (
            <button
              className="cx-campaigns-clear"
              onClick={() => setCampaignSearch("")}
            >
              Clear
            </button>
          )}
        </div>
        <div className="cx-campaigns-sort">
          <label htmlFor="campaign-sort">Sort by</label>
          <select
            id="campaign-sort"
            className="form-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recent">Most Recent</option>
            <option value="name">Name (A-Z)</option>
            <option value="contacts">Contacts Count</option>
          </select>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="cx-campaigns-tabs">
        <div className="cx-campaigns-tab-list">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`cx-campaigns-tab-btn ${
                activeTab === tab.id ? "active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="cx-campaigns-tab-icon">{tab.icon}</span>
              <span>{tab.name}</span>
              <span className="cx-campaigns-tab-count">
                {statusCounts[tab.id] ?? 0}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Campaigns Card */}
      <div className="cx-campaigns-card">
        <div className="cx-campaigns-card-header">
          <h5>Campaign Overview</h5>
          <div className="cx-campaigns-card-actions">
            <span className="text-muted me-2">
              {filteredCampaigns.length} of {campaigns.length} campaigns
            </span>
          </div>
        </div>

        {filteredCampaigns.length > 0 ? (
          <div className="cx-campaigns-list">
            {filteredCampaigns.map((campaign) => {
              const status = (campaign?.status || "sent").toLowerCase();
              const contactsCount = campaign?.contacts?.length || 0;
              return (
              <div
                key={campaign._id}
                className={`cx-campaigns-item ${
                  expandedCampaign === campaign._id ? "expanded" : ""
                }`}
              >
                <div
                  className="cx-campaigns-summary"
                  onClick={() => toggleCampaignExpand(campaign._id)}
                >
                  <div className="cx-campaigns-info">
                    <div className="cx-campaigns-name">
                      <h6>{campaign.name}</h6>
                      <span className="badge bg-light text-dark ms-2">
                        {campaign.templateId || "No template"}
                      </span>
                    </div>
                    <div className="cx-campaigns-meta">
                      <span className="cx-campaigns-meta-item">
                        <AiOutlineClockCircle className="me-1" />
                        {formatDate(campaign.schedule || campaign.createdAt)}
                      </span>
                      <span className="cx-campaigns-meta-item">
                        <AiOutlineUser className="me-1" />
                        {contactsCount} contacts
                      </span>
                      <span
                        className={`cx-campaigns-status ${status}`}
                      >
                        {getStatusIcon(status)}
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                      <span className="cx-campaigns-meta-item">
                        Updated:{" "}
                        {formatDate(campaign.updatedAt || campaign.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="cx-campaigns-actions">
                    {expandedCampaign === campaign._id ? (
                      <BiChevronUp className="cx-campaigns-expand-icon" />
                    ) : (
                      <BiChevronDown className="cx-campaigns-expand-icon" />
                    )}
                  </div>
                </div>

                {expandedCampaign === campaign._id && (
                  <div className="cx-campaigns-details">
                    <div className="cx-campaigns-detail-row">
                      <div className="cx-campaigns-detail-col">
                        <label>Contacts</label>
                        <div className="cx-campaigns-contact-list">
                          {(campaign.contacts || [])
                            .slice(0, 10)
                            .map((contact, idx) => (
                              <span
                                key={idx}
                                className="cx-campaigns-contact-badge"
                              >
                                {contact}
                              </span>
                            ))}
                          {contactsCount > 10 && (
                            <span className="cx-campaigns-contact-badge cx-campaigns-contact-more">
                              +{contactsCount - 10} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="cx-campaigns-detail-col">
                        <label>Created At</label>
                        <p>{formatDate(campaign.createdAt)}</p>
                      </div>

                      {campaign.statusSummary && ( // Use campaign.statusSummary directly
                        <div className="cx-campaigns-detail-col">
                          <label>Delivery Summary</label>
                          <p className="mb-0">
                            <span className="me-3">
                              <FiSend className="me-1 text-info" />
                              Sent: {campaign.statusSummary.sent || 0}
                            </span>
                            <span className="me-3">
                              <IoIosMail className="me-1 text-success" />
                              Delivered: {campaign.statusSummary.delivered || 0}
                            </span>
                            <span className="me-3">
                              <IoIosMailOpen className="me-1 text-primary" />
                              Read: {campaign.statusSummary.read || 0}
                            </span>
                            <span>
                              <AiOutlineCloseCircle className="me-1 text-danger" />
                              Failed: {campaign.statusSummary.failed || 0}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="cx-campaigns-detail-actions">
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => openAnalyticsModal(campaign)}
                      >
                        <FiBarChart2 className="me-1" /> Click Analytics
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => openStatusModal(campaign)}
                      >
                        <IoIosMail className="me-1" /> Message Status
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => openResendModal(campaign)}
                      >
                        <FiSend className="me-1" /> Resend Campaign
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteCampaign(campaign._id)}
                        disabled={deletingCampaignId === campaign._id}
                      >
                        {deletingCampaignId === campaign._id ? (
                          "Deleting..."
                        ) : (
                          <>
                            <AiOutlineCloseCircle className="me-1" /> Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        ) : (
          <div className="cx-campaigns-empty">
            <div className="cx-campaigns-empty-icon">
              <BiBulb />
            </div>
            <h4>No Campaigns Found</h4>
            <p className="text-muted">
              {activeTab === "all"
                ? "You haven't created any campaigns yet. Get started by creating your first campaign."
                : `No ${tabs
                    .find((t) => t.id === activeTab)
                    ?.name.toLowerCase()} campaigns found.`}
            </p>
            <button
              className="btn btn-primary"
              onClick={handleCreateNewCampaign}
            >
              <BiPlus className="me-2" /> Create Campaign
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notification;
